import React, { useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Collapse,
  Fab,
  IconButton,
  TextField,
  Typography
} from '@mui/material'
import { Visibility, ExpandLess, ExpandMore } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { StepProps } from '../../../components/_WMS/Stepper'
import { AsnLocateState, eStepAsnLocate, AsnReceptionSkuHistoryDto, WorkOrderReceptionInfoDto } from '../types'
import { GenericInputAction } from '../../../components/UI/GenericInputAction/GenericInputAction'
import { ClearAdornment } from '../../../components/UI/Adornments/ClearAdornment'
import { ScanIcon } from '../../../components/_WMS/_icons/ScanIcon'
import { NextAdornment } from '../../../components/UI/Adornments/NextAdornment'
import { useUser } from '../../../hooks/useUser'
import { useSettings } from '../../../hooks/useSettings'
import { eSetting } from '../../../enums/eSetting'
import { eTypeSetting } from '../../../enums/eTypeSetting'
import { useSnackbar } from '../../../hooks/useSnackbar'
import { SnackBar } from '../../../components/SnackBar/Snackbar'
import { Loading } from '../../../components/UI'
import { useModal } from '../../../hooks/useModal'
import { Modal } from '../../../components/_EOS/Modal'
import { OptionToggle } from '../../../components/_WMS/OptionToggle'
import { BatchTable } from '../../../components/_WMS/ListBatch/BatchTable'
import { Pagination } from '../../../components/Pagination'
import { usePagination } from '../../../hooks/usePagination'
import {
  getWorkOrder,
  checkPosition,
  validateSku,
  terminateLocateAsn,
  updateLpnState,
  getAsnHistory,
  getAsnSkuBatches
} from '../api'
import { ViewDetailStep } from './ViewDetailStep'
import { formatExpirationDate } from '../../../utils/dateHelper'
import { eTypeStorage } from '../../../enums/eTypeStorage'
import { Batch } from '../../../interfaces/Batch'
import { ApiResponse } from '../../../typings/ApiResponse'
import { eBatchState } from '../../../enums/eBatch'
import { eTypePosition } from '../../../enums/eTypePosition'

type SkuViewMode = 'SKU' | 'EAN' | 'Nombre'

interface ScannedSku {
  skuId: number
  sku: string
  ean: string
  productName: string
  qty: number
  batchId?: number
  batchNumber?: string
  batchExpirationDate?: string | null
  withExcess: boolean
}

interface LocateStepV2Props extends StepProps<AsnLocateState> {}

export const LocateStepV2: React.FC<LocateStepV2Props> = ({
  sharedState,
  setSharedState,
  goToStep,
  prev
}) => {
  const asn = sharedState.selectedResult ?? sharedState.results[0]
  const lpn = sharedState.selectedLpn ?? ''
  const hasLpn = !!lpn

  const {
    userDataState: { userData },
    getUserData
  } = useUser()
  const { settingsState, getSettings } = useSettings()
  const productOriginId = asn?.productOriginId ?? null
  const [unitaryPutaway, setUnitaryPutaway] = useState(false)
  const { isActive, isSuccess, message, openSnackBar } = useSnackbar()
  const confirmModal = useModal<string>('Confirmar Finalización')
  const backConfirmModal = useModal('¿Deseas salir?')
  const qualityModal = useModal('Estás moviendo a posición de calidad.')
  const batchModal = useModal('Lotes y Vencimientos')
  const [completedBatchInfo, setCompletedBatchInfo] = useState<string | null>(null)
  const [completedSkuInfo, setCompletedSkuInfo] = useState<string | null>(null)
  const [availableBatches, setAvailableBatches] = useState<Batch[]>([])
  const [batchSearch, setBatchSearch] = useState('')
  const [loadingBatches, setLoadingBatches] = useState(false)
  const [pendingSkuId, setPendingSkuId] = useState<number | null>(null)
  const batchPagination = usePagination(5)
  const [isFlipped, setIsFlipped] = useState(false)

  const [workOrder, setWorkOrder] = useState<WorkOrderReceptionInfoDto | null>(
    null
  )
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [validatingPosition, setValidatingPosition] = useState(false)
  const [validatingSku, setValidatingSku] = useState(false)

  // Posición
  const [positionValue, setPositionValue] = useState('')
  const [positionId, setPositionId] = useState<number | null>(null)
  const [positionStatus, setPositionStatus] = useState<
    'default' | 'success' | 'error'
  >('default')
  const [positionError, setPositionError] = useState('')
  const [isQualityPosition, setIsQualityPosition] = useState(false)

  // SKU
  const [skuValue, setSkuValue] = useState('')
  const [skuId, setSkuId] = useState<number | null>(null)
  const [skuStatus, setSkuStatus] = useState<'default' | 'success' | 'error'>(
    'default'
  )
  const [skuError, setSkuError] = useState('')
  const [skuName, setSkuName] = useState('')
  const [skuCode, setSkuCode] = useState('')
  const [skuEan, setSkuEan] = useState('')
  const [skuBatchId, setSkuBatchId] = useState<number | undefined>(undefined)
  const [skuBatchNumber, setSkuBatchNumber] = useState<string | undefined>(
    undefined
  )
  const [skuBatchExpiration, setSkuBatchExpiration] = useState<
    string | null | undefined
  >(undefined)
  const [skuPendingQty, setSkuPendingQty] = useState(0)

  // Cantidad
  const [qtyValue, setQtyValue] = useState('')

  // Lista acumulada en pantalla
  const [skuList, setSkuList] = useState<ScannedSku[]>([])
  const [viewMode, setViewMode] = useState<SkuViewMode>('SKU')

  // Historial
  const [history, setHistory] = useState<AsnReceptionSkuHistoryDto[]>([])
  const [loadingHistory, setLoadingHistory] = useState(false)

  const [headerExpanded, setHeaderExpanded] = useState(true)

  useEffect(() => {
    if (skuList.length > 0) setHeaderExpanded(false)
  }, [skuList.length])

  const skuInputRef = useRef<HTMLInputElement>(null)
  const qtyInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!userData) getUserData()

    getWorkOrder(asn?.asnId ?? 0, lpn || undefined)
      .then(({ data }) => {
        const wo = data as WorkOrderReceptionInfoDto
        if (!wo) { openSnackBar('No se encontró la orden de trabajo', false); return }
        setWorkOrder(wo)
      })
      .catch(() => openSnackBar('Error al cargar la orden de trabajo', false))
      .finally(() => setLoading(false))

    setSharedState((prev) => ({ ...prev, hideBackButton: true }))
    return () => {
      setSharedState((prev) => ({ ...prev, hideBackButton: false }))
    }
  }, [])

  useEffect(() => {
    getSettings(eTypeSetting.Recepcion, productOriginId)
  }, [productOriginId])

  useEffect(() => {
    const cfg = settingsState.settings.find(
      (s) => s.settingsMetadataId === eSetting.InboundUnitByUnit
    )
    if (cfg) setUnitaryPutaway(cfg.value === 'true')
  }, [settingsState.settings])

  // ── Cálculos de unidades ──
  const lpnDetails =
    workOrder?.workOrderDetails.filter((d) => !hasLpn || d.lpn === lpn) ?? []
  const totalControlledQty = lpnDetails.reduce(
    (a, d) => a + (d.controlledQty ?? d.realQty),
    0
  )
  const alreadyLocatedQty = lpnDetails.reduce((a, d) => a + d.locatedQty, 0)
  const enteredOnScreen = skuList.reduce((a, s) => a + s.qty, 0)
  const calculatedInbound = alreadyLocatedQty + enteredOnScreen
  const pendingQty = Math.max(0, totalControlledQty - calculatedInbound)

  // ── Posición ──
  const handleValidatePosition = (e: React.FormEvent) => {
    e.preventDefault()
    if (!positionValue.trim() || !workOrder) return
    setValidatingPosition(true)
    checkPosition(
      positionValue.trim(),
      workOrder.typeStorageId ?? eTypeStorage.Interno,
      workOrder.isReserveReception ?? false,
      workOrder.isPreSaleReception ?? false,
      workOrder.warehouseId,
      workOrder.typeOperationId ?? 0
    )
      .then(({ data }) => {
        const quality = parseInt(data.typePositionId) === eTypePosition.CalidadRecepcion
        setPositionId(parseInt(data.positionId))
        setIsQualityPosition(quality)
        setPositionStatus('success')
        setPositionError('')
        if (quality) qualityModal.openModal('sm')
        else setTimeout(() => skuInputRef.current?.focus(), 50)
      })
      .catch(({ response }) => {
        setPositionStatus('error')
        setPositionError(response?.data?.message ?? 'Posición no válida')
      })
      .finally(() => setValidatingPosition(false))
  }

  const handleClearPosition = () => {
    setPositionValue('')
    setPositionId(null)
    setPositionStatus('default')
    setPositionError('')
    setIsQualityPosition(false)
    handleClearSku()
  }

  // ── SKU ──
  const getDetailPendingQty = (skuIdVal: number, batchId?: number) => {
    const match = workOrder?.workOrderDetails.find(
      (d) => d.skuId === skuIdVal && (d.batchId ?? undefined) === (batchId ?? undefined)
    )
    return match ? match.realQty - match.locatedQty : 0
  }

  const handleValidateSku = (e: React.FormEvent) => {
    e.preventDefault()
    if (!skuValue.trim() || !positionId || !workOrder) return

    // Busca el SKU en los detalles de la work order por EAN o SKU
    const detail = workOrder.workOrderDetails.find(
      (d) => d.ean === skuValue.trim() || d.sku === skuValue.trim()
    )
    if (!detail) {
      setSkuStatus('error')
      setSkuError('SKU no encontrado en la orden')
      return
    }

    setValidatingSku(true)
    validateSku(detail.skuId, positionId)
      .then(() => {
        setSkuId(detail.skuId)
        setSkuName(detail.skuName)
        setSkuCode(detail.sku)
        setSkuEan(detail.ean)
        setSkuBatchId(detail.batchId)
        setSkuPendingQty(detail.realQty - detail.locatedQty)
        setSkuStatus('success')
        setSkuError('')
        const focusOrAutoAdd = (
          bId?: number,
          bNum?: string,
          bExp?: string | null
        ) => {
          const pendingForBatch = getDetailPendingQty(detail.skuId, bId)
          if (unitaryPutaway) {
            addToList(
              1,
              detail.skuId,
              detail.sku,
              detail.ean,
              detail.skuName,
              pendingForBatch,
              bId,
              bNum,
              bExp
            )
          } else {
            setSkuPendingQty(pendingForBatch)
            setTimeout(() => qtyInputRef.current?.focus(), 50)
          }
        }

        // Solo busca lotes si el SKU tiene batchId
        if (detail.batchId && workOrder) {
          getAsnSkuBatches(
            workOrder.asnId,
            detail.skuId,
            lpn || undefined,
            undefined,
            [eBatchState.Created, eBatchState.Pending, eBatchState.Storing]
          )
            .then(({ data }) => {
              const response = data as ApiResponse<Batch>
              const batches = response.items ?? []
              if (batches.length === 1) {
                setSkuBatchId(batches[0].batchId)
                setSkuBatchNumber(batches[0].batchNumber)
                setSkuBatchExpiration(batches[0].expirationDate)
                focusOrAutoAdd(
                  batches[0].batchId,
                  batches[0].batchNumber,
                  batches[0].expirationDate
                )
              } else if (batches.length > 1) {
                setPendingSkuId(detail.skuId)
                setAvailableBatches(batches)
                setBatchSearch('')
                batchPagination.reset()
                batchModal.openModal('sm')
              } else {
                focusOrAutoAdd()
              }
            })
            .catch(() => focusOrAutoAdd())
        } else {
          focusOrAutoAdd()
        }
      })
      .catch(({ response }) => {
        setSkuStatus('error')
        setSkuError(
          response?.data?.message ?? 'SKU no válido para esta posición'
        )
      })
      .finally(() => setValidatingSku(false))
  }

  const handleClearSku = () => {
    setSkuValue('')
    setSkuId(null)
    setSkuName('')
    setSkuCode('')
    setSkuEan('')
    setSkuBatchId(undefined)
    setSkuBatchNumber(undefined)
    setSkuBatchExpiration(undefined)
    setSkuStatus('default')
    setSkuError('')
    setQtyValue('')
  }

  // ── Cantidad ──
  const addToList = (
    qty: number,
    idSku: number,
    codeSku: string,
    eanSku: string,
    nameSku: string,
    pendingQtySku: number,
    bId?: number,
    bNumber?: string,
    bExpiration?: string | null
  ) => {
    setSkuList((prev) => {
      const existing = prev.find((s) => s.skuId === idSku && s.batchId === bId)
      const totalForSku = (existing?.qty ?? 0) + qty
      const withExcess = totalForSku > pendingQtySku
      const idx = prev.findIndex((s) => s.skuId === idSku && s.batchId === bId)

      if (totalForSku >= pendingQtySku && !withExcess) {
        if (bId && bNumber) {
          setCompletedBatchInfo(`Lote: ${bNumber}`)
        } else {
          setCompletedSkuInfo(`SKU: ${codeSku}`)
        }
      }

      if (idx >= 0) {
        const updated = [...prev]
        updated[idx] = {
          ...updated[idx],
          qty: updated[idx].qty + qty,
          withExcess
        }
        return updated
      }
      return [
        ...prev,
        {
          skuId: idSku,
          sku: codeSku,
          ean: eanSku,
          productName: nameSku,
          qty,
          batchId: bId,
          batchNumber: bNumber,
          batchExpirationDate: bExpiration,
          withExcess
        }
      ]
    })
    handleClearSku()
    setTimeout(() => skuInputRef.current?.focus(), 50)
  }

  const handleAddQty = () => {
    const qty = parseInt(qtyValue)
    if (!qty || qty <= 0 || !skuId) return
    addToList(
      qty,
      skuId,
      skuCode,
      skuEan,
      skuName,
      skuPendingQty,
      skuBatchId,
      skuBatchNumber,
      skuBatchExpiration
    )
  }

  const handleShowAllBatches = () => {
    if (!workOrder || !pendingSkuId) return
    setLoadingBatches(true)
    setBatchSearch('')
    batchPagination.reset()
    getAsnSkuBatches(
      workOrder.asnId,
      pendingSkuId,
      lpn || undefined,
      undefined,
      undefined,
      500
    )
      .then(({ data }) => {
        const response = data as ApiResponse<Batch>
        setAvailableBatches(response.items ?? [])
      })
      .catch(() => openSnackBar('Error al cargar lotes', false))
      .finally(() => setLoadingBatches(false))
  }

  const handleBatchSelect = (batch: Batch) => {
    setSkuBatchId(batch.batchId)
    setSkuBatchNumber(batch.batchNumber)
    setSkuBatchExpiration(batch.expirationDate)
    const pendingForBatch = getDetailPendingQty(skuId!, batch.batchId)
    setSkuPendingQty(pendingForBatch)
    batchModal.closeModal()
    if (unitaryPutaway) {
      addToList(
        1,
        skuId!,
        skuCode,
        skuEan,
        skuName,
        pendingForBatch,
        batch.batchId,
        batch.batchNumber,
        batch.expirationDate
      )
    } else {
      setTimeout(() => qtyInputRef.current?.focus(), 50)
    }
  }

  const filteredBatches = batchSearch.trim()
    ? availableBatches.filter(
        (b) =>
          b.batchNumber?.toLowerCase().includes(batchSearch.toLowerCase()) ||
          b.expirationDate?.includes(batchSearch)
      )
    : availableBatches

  const startIndex = batchPagination.page * batchPagination.pageSize
  const paginatedBatches = filteredBatches.slice(
    startIndex,
    startIndex + batchPagination.pageSize
  )

  const handleBatchPaginationChange = (page: number, pageSize: number) => {
    const auxPage = batchPagination.pageSize !== pageSize ? 0 : page
    batchPagination.setPagination(auxPage, pageSize)
  }

  const handleRemoveItem = (sku: string, batchId?: number) => {
    setSkuList((prev) =>
      prev.filter((s) => !(s.sku === sku && s.batchId === batchId))
    )
  }

  const hasExcess = skuList.some((s) => s.withExcess)

  const handleOpenDetail = () => {
    if (!workOrder) return
    setIsFlipped(true)
    setLoadingHistory(true)
    getAsnHistory(workOrder.asnId, workOrder.workOrderId, lpn || undefined)
      .then(({ data }) => setHistory(data ?? []))
      .catch(() => openSnackBar('Error al cargar el historial', false))
      .finally(() => setLoadingHistory(false))
  }

  // ── Guardar ──
  const handleSave = () => {
    if (!positionId || !userData?.userId || !workOrder || skuList.length === 0)
      return
    setSaving(true)
    terminateLocateAsn({
      workOrderId: workOrder.workOrderId,
      asnId: workOrder.asnId,
      lpn,
      positionId,
      userId: userData.userId,
      asnLocatedSkusDto: skuList.map((s) => ({
        skuId: s.skuId,
        locatedQty: s.qty,
        batchId: s.batchId
      }))
    } as any)
      .then(({ data }) => {
        const wo = data as WorkOrderReceptionInfoDto
        setWorkOrder(wo)
        setSkuList([])
        handleClearPosition()

        const remaining = wo.workOrderDetails.filter(
          (d) => d.realQty - d.locatedQty > 0
        )
        if (remaining.length === 0) {
          openSnackBar(`ASN ${wo.asnNumber} finalizada`, true)
          setTimeout(() => goToStep(eStepAsnLocate.ScanStep), 2000)
        } else {
          openSnackBar('Ubicación guardada', true)
        }
      })
      .catch(({ response }) => {
        openSnackBar(response?.data?.message ?? 'Error al guardar', false)
      })
      .finally(() => setSaving(false))
  }

  // ── Finalizar LPN ──
  const handleFinalizeLpn = () => {
    if (!workOrder) return
    setSaving(true)

    const doFinalize = () =>
      updateLpnState(workOrder.asnId, lpn)
        .then(() => {
          openSnackBar(`LPN ${lpn} finalizada`, true)
          setTimeout(() => prev(), 2000)
        })
        .catch(({ response }) => {
          openSnackBar(response?.data?.message ?? 'Error al finalizar LPN', false)
        })
        .finally(() => setSaving(false))

    if (skuList.length > 0 && positionId && userData?.userId) {
      terminateLocateAsn({
        workOrderId: workOrder.workOrderId,
        asnId: workOrder.asnId,
        lpn,
        positionId,
        userId: userData.userId,
        asnLocatedSkusDto: skuList.map((s) => ({
          skuId: s.skuId,
          locatedQty: s.qty,
          batchId: s.batchId
        }))
      } as any)
        .then(() => doFinalize())
        .catch(({ response }) => {
          openSnackBar(response?.data?.message ?? 'Error al guardar', false)
          setSaving(false)
        })
    } else {
      doFinalize()
    }
  }

  // ── Header ──
  const createdDate = workOrder?.asnCreatedDate
    ? new Date(workOrder.asnCreatedDate).toLocaleDateString('es-CL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    : '—'

  const chipFields = hasLpn
    ? [
        { label: 'ASN', value: workOrder?.asnNumber ?? '—' },
        { label: 'LPN', value: lpn },
        { label: 'Marca', value: workOrder?.brandName ?? '—' },
        { label: 'Creada', value: createdDate }
      ]
    : [
        { label: 'ASN', value: workOrder?.asnNumber ?? '—' },
        { label: 'Marca', value: workOrder?.brandName ?? '—' }
      ]

  const isLong = (val: string) => val.length > 15

  const getPositionPlaceholder = () => {
    if (workOrder?.isReserveReception) return 'Escanea la ubicación (RESERVA)'
    if (workOrder?.isPreSaleReception)
      return 'Escanea la ubicación (VENTA EN VERDE)'
    return 'Escanea la ubicación'
  }

  if (loading || saving || validatingPosition || validatingSku)
    return <Loading />

  // ── Vista historial ──
  if (isFlipped) {
    return (
      <ViewDetailStep
        asnNumber={workOrder?.asnNumber ?? ''}
        lpn={lpn}
        hasLpn={hasLpn}
        history={history}
        loading={loadingHistory}
        onClose={() => setIsFlipped(false)}
      />
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: '0 4px' }}>
      <div style={{ padding: '12px 16px 4px 16px', flexShrink: 0 }}>
        <Typography variant='body2' sx={{ fontWeight: 500, color: '#333' }}>
          {positionStatus !== 'success'
            ? 'Escanea o escribe una ubicación'
            : skuStatus !== 'success'
              ? 'Ingresa o selecciona un SKU/EAN'
              : 'Ingresa la cantidad'}
        </Typography>
      </div>

      {/* HEADER CARD */}
      <Box
        sx={{
          width: '100%',
          borderRadius: '10px',
          border: '1px solid #e0e0e0',
          overflow: 'hidden',
          backgroundColor: 'background.paper'
        }}
      >
        <Box
          sx={{
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1,
            py: '6px',
            minHeight: '48px'
          }}
        >
          <Fab
            variant='extended'
            size='medium'
            onClick={() =>
              skuList.length > 0 ? backConfirmModal.openModal('xs') : prev()
            }
            style={{
              borderRadius: '12px',
              height: '40px',
              paddingLeft: '10px',
              paddingRight: '14px',
              backgroundColor: 'transparent',
              border: '1px solid #E0E0E0',
              boxShadow: 'none',
              gap: '6px',
              fontSize: '14px',
              fontWeight: 500,
              color: '#666',
              textTransform: 'none'
            }}
          >
            <ArrowBackIcon style={{ fontSize: 18, color: '#1976d2' }} />
            Volver
          </Fab>
          <Fab
            variant='extended'
            size='medium'
            onClick={handleOpenDetail}
            style={{
              borderRadius: '12px',
              height: '40px',
              paddingLeft: '10px',
              paddingRight: '14px',
              backgroundColor: 'transparent',
              border: '1px solid #E0E0E0',
              boxShadow: 'none',
              gap: '6px',
              fontSize: '14px',
              fontWeight: 500,
              color: '#666',
              textTransform: 'none'
            }}
          >
            <Visibility style={{ fontSize: 18, color: '#1976d2' }} />
            Mostrar Ubicados
          </Fab>
        </Box>

        {/* Chips expandibles */}
        <Collapse in={headerExpanded}>
          {workOrder?.isPreSaleReception && (
            <Box sx={{ px: 1.5, pt: 0.75 }}>
              <Typography
                variant='body2'
                sx={{ color: 'rgb(0, 75, 177)', fontWeight: 500 }}
              >
                Recepción para venta en verde.
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              px: 1.5,
              py: '10px',
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: '6px',
              borderBottom: '1px solid #e0e0e0'
            }}
          >
            {chipFields.map(({ label, value }) => (
              <Box
                key={label}
                sx={{ gridColumn: isLong(value) ? 'span 2' : 'span 1' }}
              >
                <Chip
                  label={`${label}: ${value}`}
                  color={hasLpn ? 'primary' : 'secondary'}
                  variant='outlined'
                  style={{ fontSize: '0.8125rem', width: '100%' }}
                />
              </Box>
            ))}
          </Box>
        </Collapse>

        {/* Fila Ver más + alerta sobrantes */}
        <Box
          sx={{
            px: 1.5,
            py: '6px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #e0e0e0'
          }}
        >
          {hasExcess ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <WarningAmberIcon sx={{ color: 'error.main', fontSize: 16 }} />
              <Typography
                variant='body2'
                sx={{ color: 'error.main', fontWeight: 600 }}
              >
                Sobrantes Ingresados.
              </Typography>
            </Box>
          ) : (
            <Box />
          )}
          <Button
            size='small'
            variant='text'
            onClick={() => setHeaderExpanded((v) => !v)}
            endIcon={
              headerExpanded ? (
                <ExpandLess fontSize='small' />
              ) : (
                <ExpandMore fontSize='small' />
              )
            }
            sx={{
              textTransform: 'none',
              color: '#1976d2',
              fontWeight: 500,
              fontSize: '14px'
            }}
          >
            {headerExpanded ? 'Ver menos' : 'Ver más'}
          </Button>
        </Box>

        {/* Stats siempre visibles */}
        <Box
          sx={{
            px: 1.5,
            py: '8px',
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '8px'
          }}
        >
          {[
            { label: 'Pendientes', value: pendingQty, color: '#3B6D11' },
            { label: 'Ingresadas', value: calculatedInbound, color: '#185FA5' },
            {
              label: 'Uds. Totales',
              value: totalControlledQty,
              color: 'text.primary'
            }
          ].map(({ label, value, color }) => (
            <Box
              key={label}
              sx={{
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                p: '6px 8px',
                textAlign: 'center'
              }}
            >
              <Typography
                sx={{ fontSize: 10, color: 'text.secondary', lineHeight: 1.3 }}
              >
                {label}
              </Typography>
              <Typography
                sx={{ fontSize: 18, fontWeight: 500, color, lineHeight: 1.2 }}
              >
                {value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* INPUTS */}
      <Box sx={{ display: 'flex', flexDirection: 'column', px: '3px' }}>
        <form onSubmit={handleValidatePosition}>
          <GenericInputAction
            name='position'
            id='position'
            placeholder={getPositionPlaceholder()}
            value={positionValue}
            onChange={(e) => {
              setPositionValue(e.target.value)
              setPositionStatus('default')
            }}
            status={positionStatus}
            error={positionStatus === 'error'}
            errorText={positionError}
            backgroundColor={isQualityPosition ? '#ffd39e' : undefined}
            adornment={
              positionStatus === 'default' ? (
                <ScanIcon width={28} height={36} />
              ) : (
                <ClearAdornment
                  onClick={handleClearPosition}
                  disabled={false}
                />
              )
            }
          />
        </form>
        <form onSubmit={handleValidateSku}>
          <GenericInputAction
            name='sku'
            id='sku'
            placeholder='Escanea SKU/EAN'
            value={skuValue}
            onChange={(e) => {
              setSkuValue(e.target.value)
              setSkuStatus('default')
            }}
            status={skuStatus}
            error={skuStatus === 'error'}
            errorText={skuError}
            disabled={positionStatus !== 'success'}
            inputRef={skuInputRef}
            adornment={
              skuStatus === 'default' ? (
                <ScanIcon width={28} height={36} />
              ) : (
                <ClearAdornment onClick={handleClearSku} disabled={false} />
              )
            }
          />
        </form>
        {!unitaryPutaway && (
          <GenericInputAction
            type='number'
            name='qty'
            id='qty'
            placeholder='Ingresa cantidad'
            value={qtyValue}
            onChange={(e) => setQtyValue(e.target.value)}
            status='default'
            disabled={skuStatus !== 'success'}
            inputRef={qtyInputRef}
            onKeyPress={(e) => e.key === 'Enter' && handleAddQty()}
            adornment={<NextAdornment onClick={handleAddQty} />}
          />
        )}
      </Box>

      {/* BOTONES */}
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: '4px', px: '8px' }}
      >
        {skuList.length > 0 && (
          <Button
            fullWidth
            variant='contained'
            color='primary'
            disabled={saving}
            onClick={() => handleSave()}
          >
            Guardar y Cambiar ubicación
          </Button>
        )}
        {hasLpn && (
          <Button
            fullWidth
            variant='contained'
            disabled={saving}
            style={{ backgroundColor: '#32CD32', marginTop: '4px' }}
            onClick={() => confirmModal.openModal('sm', lpn)}
          >
            Finalizar ingreso LPN
          </Button>
        )}
      </Box>

      {/* Espacios, tamaños componente, y validar que el ingreso por cantidad desactivado, la pantalla ver detalles, hace lo mismo, muestra lo guardado con mas detalle */}

      {skuList.length > 0 && (
        <Box
          sx={{ px: '8px', display: 'flex', flexDirection: 'column', gap: 1 }}
        >
          <OptionToggle
            options={['SKU', 'EAN', 'Nombre']}
            defaultValue='SKU'
            size='small'
            onChange={(val) => setViewMode(val as SkuViewMode)}
          />
          {skuList.map((s, i) => {
            const label =
              viewMode === 'SKU'
                ? s.sku
                : viewMode === 'EAN'
                  ? s.ean
                  : s.productName
            return (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  px: 2,
                  py: 1
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Typography
                    variant='body2'
                    sx={{
                      fontWeight: 700,
                      color: s.withExcess ? 'error.main' : 'success.main'
                    }}
                  >
                    {s.qty}x&nbsp;
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{
                      fontWeight: 700,
                      color: s.withExcess ? 'error.main' : 'success.main',
                      flex: 1
                    }}
                  >
                    {label}
                  </Typography>
                  <IconButton
                    size='small'
                    onClick={() => handleRemoveItem(s.sku, s.batchId)}
                  >
                    <CloseIcon fontSize='small' />
                  </IconButton>
                </Box>
                {(s.batchNumber || s.batchExpirationDate) && (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 0.5,
                      mt: 0.5,
                      flexWrap: 'wrap'
                    }}
                  >
                    {s.batchNumber && (
                      <Chip
                        label={`Lote: ${s.batchNumber}`}
                        size='small'
                        sx={{
                          backgroundColor: '#fff9c4',
                          color: '#f57f17',
                          fontSize: '11px',
                          fontWeight: 700
                        }}
                      />
                    )}
                    {s.batchExpirationDate && (
                      <Chip
                        label={`Vence: ${formatExpirationDate(s.batchExpirationDate, 'dd-MM-yyyy')}`}
                        size='small'
                        sx={{
                          backgroundColor: '#e3f2fd',
                          color: '#1565c0',
                          fontSize: '11px',
                          fontWeight: 700
                        }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            )
          })}
        </Box>
      )}

      <Modal
        open={backConfirmModal.isOpen}
        maxWidth={backConfirmModal.size}
        title={backConfirmModal.title}
        onClose={backConfirmModal.closeModal}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography
            variant='h6'
            sx={{ textAlign: 'center', fontWeight: 600 }}
          >
            Al volver la información ingresada se eliminará. ¿Está seguro/a que
            desea eliminarla?
          </Typography>
          <Button
            fullWidth
            variant='outlined'
            onClick={() => {
              backConfirmModal.closeModal()
              prev()
            }}
            sx={{
              borderRadius: '8px',
              py: 1.2,
              textTransform: 'uppercase',
              fontWeight: 600
            }}
          >
            Sí, deseo eliminarla
          </Button>
          <Button
            fullWidth
            variant='outlined'
            onClick={backConfirmModal.closeModal}
            sx={{
              borderRadius: '8px',
              py: 1.2,
              textTransform: 'uppercase',
              fontWeight: 600
            }}
          >
            No, deseo permanecer aquí
          </Button>
        </Box>
      </Modal>

      <Modal
        open={batchModal.isOpen}
        maxWidth={batchModal.size}
        title={batchModal.title}
        disableEscapeKeyDown
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <TextField
            fullWidth
            variant='outlined'
            size='small'
            placeholder='Escanear lote o fecha...'
            value={batchSearch}
            onChange={(e) => setBatchSearch(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
          />
          <Button
            fullWidth
            variant='outlined'
            color='secondary'
            onClick={handleShowAllBatches}
            disabled={loadingBatches}
            sx={{ borderRadius: '8px', textTransform: 'uppercase' }}
          >
            Mostrar todos los lotes
          </Button>
          <BatchTable
            batches={paginatedBatches}
            showBatchNumber
            showExpirationDate
            isLoading={loadingBatches}
            onBatchClick={handleBatchSelect}
            emptyText='No se encontraron lotes'
          />
          <Pagination
            pagination={{
              page: batchPagination.page,
              pageSize: batchPagination.pageSize,
              totalItem: filteredBatches.length
            }}
            onPaginationChange={handleBatchPaginationChange}
          />
        </Box>
      </Modal>

      <Modal
        open={qualityModal.isOpen}
        maxWidth={qualityModal.size}
        title={qualityModal.title}
        onClose={qualityModal.closeModal}
        cancelButton={{
          action: () => {
            qualityModal.closeModal()
            handleClearPosition()
          },
          title: 'Cancelar'
        }}
        successButton={{
          action: () => {
            qualityModal.closeModal()
            setTimeout(() => skuInputRef.current?.focus(), 50)
          },
          title: 'Aceptar'
        }}
      >
        <Typography variant='body1' style={{ color: 'red' }}>
          Has ingresado una ubicación de calidad, ¿Deseas ingresar productos en
          esta ubicación?
        </Typography>
      </Modal>

      <Modal
        open={confirmModal.isOpen}
        maxWidth={confirmModal.size}
        title={confirmModal.title}
        onClose={confirmModal.closeModal}
        loading={saving}
        successButton={{
          action: () => {
            confirmModal.closeModal()
            handleFinalizeLpn()
          },
          title: 'Confirmar'
        }}
        cancelButton={{ action: confirmModal.closeModal, title: 'Cancelar' }}
      >
        <span>
          ¿Desea finalizar la LPN: <strong>{lpn}</strong>?
          {pendingQty > 0 && (
            <>
              <br />
              Aún restan <strong>{pendingQty}</strong> unidades por ingresar.
            </>
          )}
        </span>
      </Modal>

      <Modal
        open={!!completedBatchInfo}
        title='Lote Completado'
        maxWidth='sm'
        onClose={() => { setCompletedBatchInfo(null); setTimeout(() => skuInputRef.current?.focus(), 100) }}
        successButton={{ action: () => { setCompletedBatchInfo(null); setTimeout(() => skuInputRef.current?.focus(), 100) }, title: 'Continuar' }}
      >
        <Typography variant='body1' sx={{ mb: 1 }}>¡Has completado el total de unidades pactadas para este lote!</Typography>
        <Typography variant='body2' sx={{ fontWeight: 'bold', color: '#2196f3' }}>{completedBatchInfo}</Typography>
        <Typography variant='body2' sx={{ mt: 2, color: '#666' }}>Puedes continuar ubicando productos para otros lotes</Typography>
      </Modal>

      <Modal
        open={!!completedSkuInfo}
        title='SKU Completado'
        maxWidth='sm'
        onClose={() => { setCompletedSkuInfo(null); setTimeout(() => skuInputRef.current?.focus(), 100) }}
        successButton={{ action: () => { setCompletedSkuInfo(null); setTimeout(() => skuInputRef.current?.focus(), 100) }, title: 'Continuar' }}
      >
        <Typography variant='body1' sx={{ mb: 1 }}>¡Has completado el total de unidades para este SKU!</Typography>
        <Typography variant='body2' sx={{ fontWeight: 'bold', color: '#2196f3' }}>{completedSkuInfo}</Typography>
        <Typography variant='body2' sx={{ mt: 2, color: '#666' }}>Puedes continuar ubicando productos para otros SKUs</Typography>
      </Modal>

      <SnackBar
        message={message}
        open={isActive}
        status={isSuccess ? 'success' : 'error'}
      />
    </Box>
  )
}
